import { useState, useCallback } from 'react';
import type { UseAdminAppReturn } from './useAdminApp';

export interface Prompt {
  promptId: string;
  accountId: string;
  workspaceId: string | null;
  participantId: string | null;
  key: string;
  refType: string;
  refId: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface PromptAdminHandler {
  prompts: Prompt[];
  loading: boolean;
  error: string | null;
  listPrompts: (accountId: string, workspaceId?: string, participantId?: string) => Promise<void>;
  upsertPrompt: (data: {
    accountId: string;
    workspaceId?: string;
    participantId?: string;
    key: string;
    refType: string;
    refId: string;
    metadata?: Record<string, unknown>;
  }) => Promise<void>;
  uploadAndUpsertPrompt: (params: {
    file: File;
    key: string;
    accountId: string;
    workspaceId?: string;
    participantId?: string;
  }) => Promise<void>;
  deletePrompt: (promptId: string) => Promise<void>;
  getSignedDownloadUrl: (promptId: string) => Promise<string | null>;
  downloadPrompt: (promptId: string, filename: string) => Promise<void>;
}

export function usePromptAdmin(adminApp: UseAdminAppReturn): PromptAdminHandler {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listPrompts = useCallback(async (accountId: string, workspaceId?: string, participantId?: string) => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        accountId,
        limit: '100',
        offset: '0',
      });
      if (workspaceId) {
        params.set('workspaceId', workspaceId);
      }
      if (participantId) {
        params.set('participantId', participantId);
      }
      const res = await fetch(`${adminApp.httpUrl}/api/prompts?${params}`, {
        credentials: 'include',
        ...(workspaceId && { headers: { 'X-Workspace-Id': workspaceId } }),
      });
      if (!res.ok) throw new Error(`Failed to list prompts: ${res.status}`);
      const data = await res.json();
      setPrompts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const upsertPrompt = useCallback(async (data: {
    accountId: string;
    workspaceId?: string;
    participantId?: string;
    key: string;
    refType: string;
    refId: string;
    metadata?: Record<string, unknown>;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${adminApp.httpUrl}/api/prompt`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Failed to upsert prompt: ${res.status}`);
      await listPrompts(data.accountId, data.workspaceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl, listPrompts]);

  const deletePrompt = useCallback(async (promptId: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ promptId });
      const res = await fetch(`${adminApp.httpUrl}/api/prompt?${params}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Workspace-Id': adminApp.workspaceId },
      });
      if (!res.ok) throw new Error(`Failed to delete prompt: ${res.status}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl, listPrompts]);

  const uploadAndUpsertPrompt = useCallback(async (params: {
    file: File;
    key: string;
    accountId: string;
    workspaceId?: string;
    participantId?: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const { file, key, accountId, workspaceId, participantId } = params;
      const contentType = 'text/plain';

      // 1. Prepare — get signed upload URL
      const scopeLevel = workspaceId ? 'workspace' : 'account';
      const authHeaders = workspaceId
        ? { 'Content-Type': 'application/json', 'X-Workspace-Id': workspaceId }
        : { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId };
      const prepareRes = await fetch(`${adminApp.httpUrl}/api/storage/upload-prepare`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: JSON.stringify({
          bucketKey: 'prompts',
          filename: key,
          contentType,
          ttl: 300,
          scopeLevel,
          ...(participantId && { participant: participantId }),
        }),
      });
      if (!prepareRes.ok) {
        const body = await prepareRes.text().catch(() => prepareRes.statusText);
        throw new Error(`Upload prepare failed: ${prepareRes.status} ${body}`);
      }
      const { uploadUrl, filename, timestamp } = await prepareRes.json() as {
        uploadUrl: string;
        filename: string;
        timestamp: number;
      };

      // 2. PUT file directly to signed URL
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (!putRes.ok) {
        const body = await putRes.text().catch(() => putRes.statusText);
        throw new Error(`Upload PUT failed: ${putRes.status} ${body}`);
      }

      // 3. Complete — write latest pointer
      const completeRes = await fetch(`${adminApp.httpUrl}/api/storage/upload-complete`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: JSON.stringify({
          bucketKey: 'prompts',
          filename,
          timestamp,
          scopeLevel,
          ...(participantId && { participant: participantId }),
        }),
      });
      if (!completeRes.ok) {
        const body = await completeRes.text().catch(() => completeRes.statusText);
        throw new Error(`Upload complete failed: ${completeRes.status} ${body}`);
      }

      // 4. Upsert prompt record
      const upsertRes = await fetch(`${adminApp.httpUrl}/api/prompt`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        body: JSON.stringify({
          accountId,
          ...(workspaceId && { workspaceId }),
          ...(participantId && { participantId }),
          key,
          refType: 'mdc',
          refId: filename,
        }),
      });
      if (!upsertRes.ok) {
        const body = await upsertRes.text().catch(() => upsertRes.statusText);
        throw new Error(`Prompt upsert failed: ${upsertRes.status} ${body}`);
      }

      await listPrompts(accountId, workspaceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl, listPrompts]);

  const getSignedDownloadUrl = useCallback(async (promptId: string): Promise<string | null> => {
    try {
      const params = new URLSearchParams({ promptId });
      const res = await fetch(`${adminApp.httpUrl}/api/prompt/sign-download?${params}`, {
        credentials: 'include',
        headers: { 'X-Workspace-Id': adminApp.workspaceId },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.downloadUrl ?? null;
    } catch {
      return null;
    }
  }, [adminApp.httpUrl]);

  const downloadPrompt = useCallback(async (promptId: string, filename: string) => {
    const url = await getSignedDownloadUrl(promptId);
    if (!url) {
      setError('Failed to get download URL');
      return;
    }
    const res = await fetch(url);
    if (!res.ok) {
      setError(`Download failed: ${res.status}`);
      return;
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${filename}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [getSignedDownloadUrl]);

  return {
    prompts,
    loading,
    error,
    listPrompts,
    upsertPrompt,
    uploadAndUpsertPrompt,
    deletePrompt,
    getSignedDownloadUrl,
    downloadPrompt,
  };
}
